import { useState, useContext, useMemo, useCallback } from 'react'
import { Route, Switch, useHistory, useParams } from 'react-router-dom'
import { useQuery } from 'react-query'
import { useTranslation } from 'react-i18next'
import { Select } from 'antd'
import { ActionMenu, Notification, SlidePanel, ButtonLoader, Button, NewTable, PageView, PageTopBar, DialogView } from '../../components'
import { Loader } from '../../components/Loader'
import { UserContext } from '../../hooks/UserContext'
import { fetchOperators, createOperator, updateOperatorStatus, updateRoles, updateWarehouses, fetchOperatorsAccount } from '../../services/operatorServices'
import { fetchWarehouses } from '../../services/warehouseSevices'
import { OPERATORS, CONFIGURATIONS_OPERATORS, CONFIGURATIONS_OPERATORS_DETAIL } from '../../navigation/constants'
import { OperatorDetailContainer } from './OperatorDetailContainer'
import { setOperatorsPageSize } from '../../redux/pageSizeSlice'
import { useDispatch, useSelector } from 'react-redux'
import { useRef } from 'react'
import { Checkbox } from '../../components'
import DropdownMenu from '../../components/DropdownMenu'
import { isSuperAdminUser } from '../../utils/userUtils'
import { getLoginMethod } from '../../services/loginServices'

// Every role that lets a user into the OMS: each one grants HOST_ACCESS_CUSTOMER_APP on the platform.
// ROLE_SUPER_ADMIN also gets in (through HOST_ACCESS_ALL) but must never be listed: disabling OMS access
// removes every role here, so listing it would demote a super admin.
const OMS_ACCESS_ROLES = ['ROLE_CUSTOMER', 'ROLE_STORE_ADMIN', 'ROLE_STORE_COLLABORATOR', 'ROLE_STORE_ANALYST', 'ROLE_WH_ADMIN_OMS_ACCESS']
const grantsOmsAccess = roles => roles.some(role => OMS_ACCESS_ROLES.includes(role))

/*
const columns = [
  {
    Header: "Nombre",
    accessor: "full_name", // accessor is the "key" in the data
    searchAs: "full_name",
    disableFilters: false,
    disableSortBy: false,
  },
  {
    Header: "Email",
    accessor: "email", // accessor is the "key" in the data
    searchAs: "email",
    disableFilters: true,
    disableSortBy: false,
  },
  {
    Header: "Rol asignado",
    accessor: "rol", // accessor is the "key" in the data
    disableFilters: true,
    disableSortBy: false,
  },
  {
    Header: "Estado",
    accessor: "estado", // accessor is the "key" in the data
    Filter: ({ column: { setFilter, filterValue } }) => {
      const toggleCheckboxChange = () => {
        setFilter(!filterValue);
      };

      return (
        <div>
          <Checkbox
            label="Mostrar sólo usuarios activos"
            checked={filterValue || false}
            onChange={toggleCheckboxChange}
            size={5}
          />
        </div>
      );
    },
    searchAs: "return_type",
    disableFilters: false,
    disableSortBy: false,
  },
  /* {
     Header: "Estado",
     accessor: "estado", // accessor is the "key" in the data
     disableFilters: true,
     disableSortBy: true,
   },
  {
    Header: "",
    accessor: "actions", // accessor is the "key" in the data
    disableFilters: true,
    disableSortBy: true,
    fetchingIndicator: true,
    shrinkToContent: true,
  }
]
*/

/*

const CustomCheckboxFilter = ({ column: { setFilter, filterValue } }) => {
  return (
    <div>
      <Checkbox
        checked={filterValue || false}
        onChange={(e) => {
          setFilter(e.target.checked ? true : undefined);
        }}
      />
    </div>
  );
};
*/

export const ListOperatorsContainer = () => {
  const { user } = useContext(UserContext)
  const tableRef = useRef()
  const history = useHistory()
  const { warehouse_id } = useParams()
  const dispatch = useDispatch()
  const pageSize = useSelector(state => state.pageSize.operatorsList)

  const isCurrentUserSuperAdmin = isSuperAdminUser(user)

  // Build initial search params with warehouse_ids filter
  const getInitialSearchParams = () => {
    const baseParams = {
      page: 1,
      per_page: pageSize,
      warehouse_id: warehouse_id,
    }

    // Add warehouse_ids filter if user is not super admin
    if (!isCurrentUserSuperAdmin && user?.warehouse_ids && user.warehouse_ids.length > 0) {
      baseParams.warehouse_ids = user.warehouse_ids
    }

    return baseParams
  }

  const initialSearchParams = getInitialSearchParams()
  const [searchParams, setSearchParams] = useState({ ...initialSearchParams, is_enabled: true })
  const [showNewOperatorForm, setShowNewOperatorForm] = useState(false)
  const [showClipboardCopied, setShowClipboardCopied] = useState(false)
  const [showOperatorCreated, setOperatorCreated] = useState(false)
  const [loadOperatorCreation, setLoadOperatorCreation] = useState(false)
  const [userName, setUserName] = useState(null)
  const [userLastname, setUserLastname] = useState(null)
  const [userEmail, setUserEmail] = useState(null)
  const [userPassword, setUserPassword] = useState(null)
  // SSO-enforced emails (bare @cubbo.com) sign in with Google, so the backend mints no password.
  // The address discovery confirmed; an answer for an email edited since simply stops matching.
  const [ssoEmail, setSsoEmail] = useState(null)
  const userEmailIsSso = !!ssoEmail && ssoEmail === userEmail?.trim()
  const [selectedWarehouses, setSelectedWarehouses] = useState([])
  const [selectedRoles, setSelectedRoles] = useState(['ROLE_OPERATOR'])
  const [warehousesError, setWarehousesError] = useState(false)
  const [showOperatorRolesUpdatedSuccess, setOperatorRolesUpdatedSuccess] = useState(false)
  const [showOperatorRolesUpdatedFailed, setOperatorRolesUpdatedFailed] = useState(false)
  const [operatorRolesUpdateError, setOperatorRolesUpdateError] = useState('')
  const [showCustomerAppEnabledSuccess, setCustomerAppEnabledSuccess] = useState(false)
  const [showCustomerAppEnabledFailed, setCustomerAppEnabledFailed] = useState(false)
  const [showOperatorWarehousesUpdatedSuccess, setOperatorWarehousesUpdatedSuccess] = useState(false)
  const [showOperatorWarehousesUpdatedFailed, setOperatorWarehousesUpdatedFailed] = useState(false)
  const [showOperatorStatusUpdateFailed, setOperatorStatusUpdateFailed] = useState(false)
  const [showOperatorEditRefused, setOperatorEditRefused] = useState(false)
  const [showOperatorCreationFailed, setOperatorCreationFailed] = useState(false)
  const [operatorCreationError, setOperatorCreationError] = useState('')

  const { i18n } = useTranslation()

  const OPERATOR_ROLES = [
    {
      role: 'ROLE_OPERATOR',
      name: 'Operador',
      description:
        'Tiene permisos para llevar a cabo funciones dentro del sistema de gestión de almacén, incluyendo la lectura y seguimiento de pedidos o productos hasta la manipulación y transferencia de inventario, así como la generación y actualización de etiquetas de envío.',
    },
    {
      role: 'ROLE_LAST_MILE_SUPERVISOR',
      name: 'Supervisor de última milla',
      description:
        'Tiene autorización para supervisar y gestionar aspectos de la última etapa del proceso de envío en la cadena de suministro, incluyendo el manejo de incidentes de envío y reclamaciones, así como el acceso a información sobre pedidos, productos y el estado general del almacén.',
    },
    {
      role: 'ROLE_LAST_MILE_OPERATOR',
      name: 'Operador de última milla',
      description: 'Tiene permisos para registrar eventos de operaciones en lastmile desde la Handheld.',
    },
    {
      role: 'ROLE_INVENTORY_SUPERVISOR',
      name: 'Supervisor de inventario',
      description:
        'Tiene permisos para supervisar y gestionar diversos aspectos del inventario en el almacén, incluyendo recuentos, transferencias, reabastecimientos, historial y revisiones de órdenes de recogida, entre otras funciones críticas para el control efectivo del inventario.',
    },
    {
      role: 'ROLE_REVERSE_LOGISTICS_OPERATOR',
      name: 'Operador de logística inversa',
      description:
        'Tiene permisos para gestionar y supervisar diversas actividades de logística inversa y devoluciones en el almacén, incluyendo la creación de reposiciones de inventario, recepción y manejo de devoluciones, gestión de pedidos enviados y transferencia de ubicaciones de almacenamiento.',
    },
    {
      role: 'ROLE_INVENTORY_REVIEWER',
      name: 'Revisor de inventario',
      description:
        'Tiene permisos para revisar conteos ciclicos con divergencias, realizar reconteos de verificacion y corregir posiciones de inventario mediante transferencias.',
    },
    {
      role: 'ROLE_WH_ADMIN',
      name: 'Administrador de almacén',
      description:
        'Acceso total a todas las áreas del almacén (usuarios, roles, inventario, pedidos y configuración). Solo un administrador de almacén, administrador o superadministrador puede asignar este rol.',
    },
  ]

  const OPERATOR_ROLES_FILTERS = [
    {
      role: 'ROLE_SUPER_ADMIN',
      name: 'Súper administrador',
    },
    {
      role: 'ROLE_LAST_MILE_SUPERVISOR',
      name: 'Supervisor de última milla',
    },
    {
      role: 'ROLE_INVENTORY_SUPERVISOR',
      name: 'Supervisor de inventario',
    },
    {
      role: 'ROLE_REVERSE_LOGISTICS_OPERATOR',
      name: 'Operador de logística inversa',
    },
    {
      role: 'ROLE_INVENTORY_REVIEWER',
      name: 'Revisor de inventario',
    },
    {
      role: 'ROLE_WH_ADMIN',
      name: 'Administrador de almacén',
    },
  ]

  // isCurrentUserSuperAdmin já foi definido no início do componente

  const warehousesQueryParams = useMemo(() => {
    return { page: 1, per_page: 1000 }
  }, [])

  const { data: warehousesData, isLoading: isLoadingWarehouses } = useQuery(
    ['warehouses', warehousesQueryParams],
    () => fetchWarehouses(warehousesQueryParams),
    {
      keepPreviousData: true,
      enabled: !!user,
    },
  )

  const { isLoading, isError, error, data, isFetching, isPreviousData, refetch } = useQuery(['operators', searchParams], () => fetchOperators(searchParams), {
    keepPreviousData: true,
    enabled: !!warehouse_id, // Only fetch when warehouse_id is available
  })

  const resetNewOperatorFields = () => {
    setUserName('')
    setUserLastname('')
    setUserEmail('')
    setUserPassword('')
    setSelectedWarehouses([])
    setSelectedRoles(['ROLE_OPERATOR'])
    setWarehousesError(false)
  }

  const handleOnCreateOperator = async () => {
    // Validate that at least one warehouse is selected
    if (!selectedWarehouses || selectedWarehouses.length === 0) {
      setWarehousesError(true)
      return
    }

    await createOperatorRequest()
  }

  const createOperatorRequest = async () => {
    setLoadOperatorCreation(true)
    const operatorsAccount = await fetchOperatorsAccount(searchParams)
    if (operatorsAccount) {
      try {
        const payload = {
          name: userName,
          lastname: userLastname,
          email: userEmail?.trim(),
          password: userPassword,
          account_id: operatorsAccount.id,
          roles: selectedRoles,
          warehouse_ids: selectedWarehouses,
        }
        const response = await createOperator(payload)
        refetch()
        setOperatorCreated(true)
        resetNewOperatorFields()
        setShowNewOperatorForm(false)
      } catch (error) {
        const code = error?.response?.data?.code
        const translatedCodes = {
          USER_EXISTS: 'list_operators.user_exists',
          sso_required: 'list_operators.sso_required',
          ACCOUNT_OUTSIDE_DOMAIN: 'list_operators.account_outside_domain',
        }
        if (translatedCodes[code]) {
          setOperatorCreationError(i18n.t(translatedCodes[code]))
          setOperatorCreationFailed(true)
          return
        }
        const backendError = error?.response?.data?.error

        // Backend returns the password validation error as a Ruby-hash string
        // (e.g. `{"rules"=>[...]}`). Convert `=>` to `:` and strip invalid JSON
        // escape sequences (e.g. `\#` from "e.g. !@\#$%^&*") so JSON.parse works.
        let parsedError = backendError
        if (typeof backendError === 'string') {
          try {
            const cleaned = backendError
              .replace(/=>/g, ':')
              .replace(/\bnil\b/g, 'null')
              .replace(/\\(?!["\\/bfnrtu])/g, '')
            parsedError = JSON.parse(cleaned)
          } catch (_) {
            parsedError = backendError
          }
        }

        let backendMessage = null
        if (parsedError?.rules) {
          const translateOrFallback = (key, options, fallback) => {
            const translated = i18n.t(key, options)
            return translated === key ? fallback : translated
          }
          const formatRuleMessage = rule => {
            const interpolation = {}
            if (rule.code === 'lengthAtLeast') {
              interpolation.count = rule.format?.[0] ?? 8
            } else if (rule.code === 'containsAtLeast') {
              interpolation.min = rule.format?.[0] ?? 3
              interpolation.total = rule.format?.[1] ?? 4
            }
            let i = 0
            const fallbackMessage = (rule.message || '').replace(/%[ds]/g, () => rule.format?.[i++] ?? '')
            const message = translateOrFallback(`list_operators.password_rules.${rule.code}`, interpolation, fallbackMessage)

            const failedItems =
              rule.items
                ?.filter(item => !item.verified)
                .map(item => translateOrFallback(`list_operators.password_rules.items.${item.code}`, {}, item.message)) || []

            return failedItems.length ? `${message} ${failedItems.join(', ')}` : message
          }
          backendMessage = parsedError.rules
            .filter(rule => !rule.verified)
            .map(formatRuleMessage)
            .join('. ')
        } else if (typeof parsedError === 'string') {
          backendMessage = parsedError
        } else if (parsedError?.message) {
          backendMessage = parsedError.message
        } else if (parsedError) {
          console.error('Unexpected error format:', parsedError)
          backendMessage = i18n.t('list_operators.notifications.password_reset_error')
        }
        setOperatorCreationError(backendMessage)
        setOperatorCreationFailed(true)
      } finally {
        setLoadOperatorCreation(false)
      }
    } else {
      console.error('operators_account.id no disponible aún.')
      setLoadOperatorCreation(false)
    }
  }

  const handlerOnClickReset = user => {
    history.push({
      pathname: CONFIGURATIONS_OPERATORS_DETAIL.replace(':warehouse_id', warehouse_id),
      search: '?query=abc',
      state: { user: user, warehouseMap: warehouseMap },
    })
  }

  // The platform refuses edits on Cubbo staff accounts by admins outside Cubbo with a 401. A refusal
  // is permanent, so it gets its own notice instead of the "try again" one; anything else is a
  // failed save. Used by the status, OMS-access and warehouse mutations below.
  const notifyMutationFailure = (error, setFailed) => {
    if (error?.response?.status === 401) setOperatorEditRefused(true)
    else setFailed(true)
  }

  const handlerOnClickUpdateOperatorStatus = async user => {
    try {
      await updateOperatorStatus(user.id, !user.is_enabled)
      refetch()
    } catch (error) {
      notifyMutationFailure(error, setOperatorStatusUpdateFailed)
    }
  }

  const getOrderBy = orderName => {
    switch (orderName) {
      case 'full_name_or_email':
        return 'first_name'
      case 'estado':
        return 'is_enabled'
      default:
        return orderName
    }
  }

  const onSortChange = orderBy => {
    if (orderBy.length > 0) {
      setSearchParams({
        ...searchParams,
        order_by: getOrderBy(orderBy[0].id),
        order: orderBy[0].desc ? 'DESC' : 'ASC',
      })
    } else {
      if ('order_by' in searchParams) {
        delete searchParams.order_by
        delete searchParams.order
        setSearchParams({ ...searchParams })
      }
    }
  }

  const updateFiltersData = (updatedData, filter) => {
    // Update filters with new data
    setFiltersData(prev => {
      return prev
        .map(item => {
          if (item.key == filter)
            return {
              ...item,
              data: updatedData,
            }
          else return item
        })
        .sort((a, b) => (a.data.visible > b.data.visible ? -1 : 1)) // Sort to preserve the order of the filters
    })

    // Set search params only if needed
    const filterItem = filtersData.find(item => item.key == filter)
    if (Array.isArray(filterItem.data.value) && filterItem.data.value.length === 0 && updatedData.value.length == 0) return
    if (!['boolean', 'text'].includes(filterItem.data.type) && filterItem.data.value === updatedData.value) return
    const value = updatedData.value
    const filter_name = filterItem.key_alias || filterItem.key
    setSearchParams(prev => ({ ...prev, [filter_name]: value, page: 1 }))
  }

  const handleCustomerAppEnabledNotification = status => {
    if (status === 200) {
      setCustomerAppEnabledSuccess(true)
      refetch()
    } else {
      setCustomerAppEnabledFailed(true)
    }
  }

  // Flips OMS access on the user's FULL role array. Passing the full array preserves other roles —
  // notably ROLE_WH_ADMIN, which the backend requires the ROLE_WH_ADMIN_OMS_ACCESS add-on to be paired with.
  // Disabling removes every OMS role, not only the one enabling adds: the OMS user settings assign the
  // store roles, and any one left behind still lets the user in. Enabling adds `enableRole`: ROLE_CUSTOMER
  // (customer-app) for regular users and ROLE_WH_ADMIN_OMS_ACCESS for WH admins.
  const toggleOmsAccess = async (userId, current_roles, enableRole) => {
    const new_roles = grantsOmsAccess(current_roles) ? current_roles.filter(r => !OMS_ACCESS_ROLES.includes(r)) : [...current_roles, enableRole]

    try {
      const res = await updateRoles(userId, new_roles)
      handleCustomerAppEnabledNotification(res.status)
    } catch (error) {
      notifyMutationFailure(error, setCustomerAppEnabledFailed)
    }
  }

  const getMenuOptions = (user, isListUserSuperAdmin) => {
    // Warehouse admins get OMS access through the dedicated ROLE_WH_ADMIN_OMS_ACCESS add-on
    // (scoped to their warehouse's stores); everyone else uses the customer-app ROLE_CUSTOMER toggle.
    const isRowWhAdmin = user.roles.includes('ROLE_WH_ADMIN')
    const hasOmsAccess = grantsOmsAccess(user.roles)
    const omsAccessOption = isRowWhAdmin
      ? {
          title: hasOmsAccess ? i18n.t('list_operators.disable_wh_admin_oms_access') : i18n.t('list_operators.wh_admin_oms_access'),
          className: 'text-red',
          clickHandler: () => {
            toggleOmsAccess(user.id, user.roles, 'ROLE_WH_ADMIN_OMS_ACCESS')
          },
        }
      : {
          title: hasOmsAccess ? i18n.t('list_operators.disable_customer_app_access') : i18n.t('list_operators.customer_app_access'),
          className: 'text-red',
          clickHandler: () => {
            toggleOmsAccess(user.id, user.roles, 'ROLE_CUSTOMER')
          },
        }
    const options = [
      omsAccessOption,
      {
        title: i18n.t('list_operators.reset_password'),
        clickHandler: () => handlerOnClickReset(user),
      },
      {
        title: user.is_enabled ? i18n.t('list_operators.disable_user') : i18n.t('list_operators.enable_user'),
        className: 'text-red',
        clickHandler: () => {
          if (!isListUserSuperAdmin) {
            handlerOnClickUpdateOperatorStatus(user)
          }
        },
      },
    ]
    if (isListUserSuperAdmin) {
      options[2].title = <span style={{ color: 'lightgray' }}>{options[2].title}</span>
    }
    return options
  }

  const generatePwd = id => {
    const firstBlockChars = 'abcdefghijklmnopqrstuvwxyz'
    const firstBlockPasswordLength = 3
    const secondBlockChars = '0123456789'
    const secondBlockPasswordLength = 2
    const thirdBlockChars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
    const thirdBlockPasswordLength = 2
    let password = ''
    for (let i = 0; i <= firstBlockPasswordLength; i++) {
      const randomNumber = Math.floor(Math.random() * firstBlockChars.length)
      password += firstBlockChars.substring(randomNumber, randomNumber + 1)
    }
    password += '@'
    for (let i = 0; i <= secondBlockPasswordLength; i++) {
      const randomNumber = Math.floor(Math.random() * secondBlockChars.length)
      password += secondBlockChars.substring(randomNumber, randomNumber + 1)
    }
    for (let i = 0; i <= thirdBlockPasswordLength; i++) {
      const randomNumber = Math.floor(Math.random() * thirdBlockChars.length)
      password += thirdBlockChars.substring(randomNumber, randomNumber + 1)
    }
    setUserPassword(password)
  }

  const handleResetFilters = () => {
    setSearchParams(initialSearchParams)
  }

  const handleUpdatedRolesNotification = status => {
    if (status === 200) {
      setOperatorRolesUpdatedSuccess(true)
    } else {
      setOperatorRolesUpdatedFailed(true)
    }
  }

  // The column edits only OPERATOR_ROLES, but update_roles replaces the whole array: every other
  // role travels back untouched. The OMS add-on cannot outlive ROLE_WH_ADMIN (the platform rejects it).
  // A WH admin reaches the OMS through that add-on, never ROLE_CUSTOMER (same rule as the create form),
  // so promoting an operator who had OMS access swaps one for the other.
  // Returns whether the save landed, so DropdownMenu can put the saved selection back when it did not.
  // ponytail: merges against the roles loaded with the row, so a change made elsewhere since the last
  // fetch is overwritten; a server-side merge of warehouse roles removes that window.
  const handleRolesUpdate = async (operator, selectedRoles) => {
    const currentRoles = operator.roles || []
    const operatorRoleKeys = OPERATOR_ROLES.map(({ role }) => role)
    const promotedFromCustomer = selectedRoles.includes('ROLE_WH_ADMIN') && !currentRoles.includes('ROLE_WH_ADMIN') && currentRoles.includes('ROLE_CUSTOMER')
    const keptRoles = currentRoles.filter(
      role =>
        !operatorRoleKeys.includes(role) &&
        !(role === 'ROLE_WH_ADMIN_OMS_ACCESS' && !selectedRoles.includes('ROLE_WH_ADMIN')) &&
        !(role === 'ROLE_CUSTOMER' && promotedFromCustomer),
    )
    const omsAddOn = promotedFromCustomer && !keptRoles.includes('ROLE_WH_ADMIN_OMS_ACCESS') ? ['ROLE_WH_ADMIN_OMS_ACCESS'] : []
    setOperatorRolesUpdateError('')
    try {
      const res = await updateRoles(operator.id, [...keptRoles, ...omsAddOn, ...selectedRoles])
      handleUpdatedRolesNotification(res.status)
      return res.status === 200
    } catch (error) {
      const reason = error?.response?.data?.error
      setOperatorRolesUpdateError(typeof reason === 'string' ? reason : '')
      setOperatorRolesUpdatedFailed(true)
      return false
    } finally {
      refetch()
    }
  }

  // Returns whether the save landed, so DropdownMenu puts the saved selection back when it did not.
  const handleWarehousesUpdate = async (operatorId, warehouseIds) => {
    try {
      const res = await updateWarehouses(operatorId, warehouseIds)
      handleUpdatedWarehousesNotification(res.status)
      refetch()
      return res.status === 200
    } catch (error) {
      notifyMutationFailure(error, setOperatorWarehousesUpdatedFailed)
      return false
    }
  }

  const handleUpdatedWarehousesNotification = status => {
    if (status === 200) {
      setOperatorWarehousesUpdatedSuccess(true)
    } else {
      setOperatorWarehousesUpdatedFailed(true)
    }
  }

  const operatorRolesFilters = OPERATOR_ROLES_FILTERS.filter(role => role.filterable !== false).map(role => ({ id: role.role, label: role.name }))

  // Prepare warehouse options for the logged-in user
  const availableWarehousesForUser = useMemo(() => {
    if (!warehousesData?.warehouses || !user) return []

    // If user is super admin, return all warehouses
    if (isCurrentUserSuperAdmin) {
      return warehousesData.warehouses.map(wh => ({
        id: wh.id,
        name: wh.name,
      }))
    }

    // Otherwise, filter warehouses based on user's warehouse_ids
    if (user?.warehouse_ids && user.warehouse_ids.length > 0) {
      return warehousesData.warehouses
        .filter(wh => user.warehouse_ids.includes(wh.id))
        .map(wh => ({
          id: wh.id,
          name: wh.name,
        }))
    }

    return []
  }, [warehousesData?.warehouses, user, isCurrentUserSuperAdmin])

  // Create warehouse ID to name mapping
  const warehouseMap = useMemo(() => {
    if (!warehousesData?.warehouses) return {}
    return warehousesData.warehouses.reduce((acc, warehouse) => {
      acc[warehouse.id] = warehouse.name
      return acc
    }, {})
  }, [warehousesData?.warehouses])

  const memoizedTableData = useMemo(() => {
    return data?.users?.map(user => {
      const isListUserSuperAdmin = isSuperAdminUser(user)
      const isListUserAdmin = user.roles?.some(role => ['ROLE_ADMIN', 'ROLE_WH_ADMIN'].includes(role))

      // Determine warehouses display for editable dropdown
      let warehousesDisplay = null

      if (isListUserSuperAdmin) {
        // Super admin cannot be edited
        warehousesDisplay = <div className="font-normal text-base text-gray-700">{i18n.t('list_operators.all_warehouses')}</div>
      } else {
        // Regular operators can have their warehouses edited
        warehousesDisplay = (
          <DropdownMenu
            options={availableWarehousesForUser}
            selected={user.warehouse_ids || []}
            element={user.id}
            action={handleWarehousesUpdate}
            isSuperAdminOrAdmin={false}
            isWarehouseMode={true}
          />
        )
      }

      const getUserBadge = () => {
        if (isListUserSuperAdmin) {
          return <div className="inline-flex items-center px-3 py-1 rounded text-xs font-semibold bg-yellow-400 text-white">Super admin</div>
        }
        if (isListUserAdmin) {
          return <div className="inline-flex items-center px-3 py-1 rounded text-xs font-semibold bg-green-400 text-white">Admin</div>
        }
        return <div className="inline-flex items-center px-3 py-1 rounded text-xs font-semibold bg-blue-500 text-white">Operador</div>
      }

      const operatorFullNameOrSupervisorFullNameWithBadge = (
        <>
          <div className="text-base mb-1">
            {user.first_name} {user.last_name}
          </div>
          {getUserBadge()}
        </>
      )

      return {
        key: user.id,
        object: user,
        full_name_or_email: operatorFullNameOrSupervisorFullNameWithBadge,
        email: <div className="font-normal text-base text-gray-700">{user.email}</div>,
        roles: (
          <DropdownMenu
            options={OPERATOR_ROLES}
            selected={(user.roles || []).filter(user_role => OPERATOR_ROLES.some(operator_role => operator_role.role === user_role))}
            element={user.id}
            action={(_, selectedRoles) => handleRolesUpdate(user, selectedRoles)}
            // should not be updatable if list user is super admin, unless current user is also a super admin
            isSuperAdminOrAdmin={!isCurrentUserSuperAdmin && isListUserSuperAdmin}
          />
        ),
        has_oms_access: (() => {
          const hasOmsAccess = grantsOmsAccess(user.roles)
          return <span className={`text-${hasOmsAccess ? 'green' : 'gray'}-400 font-medium`}>{hasOmsAccess ? i18n.t('common.yes') : i18n.t('common.no')}</span>
        })(),
        warehouses: warehousesDisplay,
        estado: <span className={`text-${user.is_enabled ? 'green' : 'gray'}-400 font-medium`}>{user.is_enabled ? 'Activo' : 'Deshabilitado'}</span>,
        actions:
          isCurrentUserSuperAdmin || !isListUserSuperAdmin ? <ActionMenu className="float-right" items={getMenuOptions(user, isListUserSuperAdmin)} /> : null,
      }
    })
  }, [data?.users, warehouseMap, i18n, availableWarehousesForUser])

  const memoizedColumns = useMemo(() => {
    if (!data?.users) return []

    return [
      {
        Header: 'Nombre',
        accessor: 'full_name_or_email', // accessor is the "key" in the data
      },
      {
        Header: 'Correo',
        accessor: 'email', // accessor is the "key" in the data
      },
      {
        Header: 'Roles asignados',
        accessor: 'roles', // accessor is the "key" in the data
        disableSortBy: true,
      },
      {
        Header: i18n.t('list_operators.has_oms_access'),
        accessor: 'has_oms_access', // accessor is the "key" in the data
        disableSortBy: true,
      },
      {
        Header: i18n.t('list_operators.warehouses_column'),
        accessor: 'warehouses', // accessor is the "key" in the data
        disableSortBy: true,
      },
      {
        Header: 'Estado',
        accessor: 'estado', // accessor is the "key" in the data
      },
      {
        Header: '',
        accessor: 'actions',
        disableSortBy: true,
        fetchingIndicator: true,
      },
    ]
  }, [data, i18n])

  const filtersInitialData = [
    {
      key: 'full_name_or_email',
      placeholder: i18n.t('list_operators.search_by_name'),
      label: i18n.t('list_operators.search_by_name'),
      data: {
        visible: true,
        type: 'text',
        value: '',
      },
    },
    {
      key: 'roles',
      label: i18n.t('list_operators.search_by_role'),
      data: {
        visible: true,
        type: 'multiFilter',
        value: [],
        placeholder: i18n.t('list_operators.search_by_role'),
        options: operatorRolesFilters,
      },
    },
    {
      key: 'is_enabled',
      placeholder: i18n.t('list_operators.show_active_only'),
      label: i18n.t('list_operators.show_active_only'),
      data: {
        visible: true,
        type: 'boolean',
        onClickValue: true,
      },
    },
  ]

  const [filtersData, setFiltersData] = useState(filtersInitialData)
  // The elevated WH Admin role is offered only when the backend says the current user may
  // assign it (UserSerializer#permissions.assign_wh_admin = WH admin / admin / super admin).
  // The server still enforces warehouse scope at write time.
  const canAssignWhAdmin = user?.permissions?.assign_wh_admin === true
  const assignableNewOperatorRoles = () => {
    return OPERATOR_ROLES.filter(operator_role => operator_role.role !== 'ROLE_WH_ADMIN' || canAssignWhAdmin)
  }

  return (
    <>
      <Notification show={showClipboardCopied} setShow={setShowClipboardCopied} type="success" title={i18n.t('list_operators.notifications.copied')} />
      <Notification show={showOperatorCreated} setShow={setOperatorCreated} type="success" title={i18n.t('list_operators.notifications.operator_created')} />
      <Notification
        show={showOperatorCreationFailed}
        setShow={setOperatorCreationFailed}
        type="error"
        title={i18n.t('list_operators.notifications.operator_creation_failed')}
        description={operatorCreationError}
      />
      <Notification
        show={showOperatorRolesUpdatedSuccess}
        setShow={setOperatorRolesUpdatedSuccess}
        type="success"
        title={i18n.t('list_operators.notifications.operator_roles_updated_success')}
      />
      <Notification
        show={showOperatorRolesUpdatedFailed}
        setShow={setOperatorRolesUpdatedFailed}
        type="error"
        title={i18n.t('list_operators.notifications.operator_roles_updated_failed')}
        description={operatorRolesUpdateError}
      />
      <Notification
        show={showCustomerAppEnabledSuccess}
        setShow={setCustomerAppEnabledSuccess}
        type="success"
        title={i18n.t('list_operators.notifications.customer_app_enabled_success')}
      />
      <Notification
        show={showCustomerAppEnabledFailed}
        setShow={setCustomerAppEnabledFailed}
        type="error"
        title={i18n.t('list_operators.notifications.customer_app_enabled_failed')}
      />
      <Notification
        show={showOperatorWarehousesUpdatedSuccess}
        setShow={setOperatorWarehousesUpdatedSuccess}
        type="success"
        title={i18n.t('list_operators.notifications.operator_warehouses_updated_success')}
      />
      <Notification
        show={showOperatorWarehousesUpdatedFailed}
        setShow={setOperatorWarehousesUpdatedFailed}
        type="error"
        title={i18n.t('list_operators.notifications.operator_warehouses_updated_failed')}
      />
      <Notification
        show={showOperatorStatusUpdateFailed}
        setShow={setOperatorStatusUpdateFailed}
        type="error"
        title={i18n.t('list_operators.notifications.operator_status_update_failed')}
      />
      <Notification
        show={showOperatorEditRefused}
        setShow={setOperatorEditRefused}
        type="error"
        title={i18n.t('list_operators.notifications.operator_edit_refused')}
      />
      <PageView
        topMenu={
          <PageTopBar>
            <div className="text-lg font-semibold">{i18n.t('list_operators.operators')}</div>
            <Button type="primary" onClick={() => setShowNewOperatorForm(true)}>
              {i18n.t('list_operators.new_operator')}
            </Button>
          </PageTopBar>
        }
        childrenFullWidth={true}
        topMenuFullWidth={true}
      >
        {!isPreviousData && isLoading ? (
          <Loader show={true}></Loader>
        ) : isError ? (
          <div>Error: {error.message}</div>
        ) : (
          <>
            <DialogView open={showNewOperatorForm} setOpen={setShowNewOperatorForm} closeOnBackdrop={true}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold">{i18n.t('list_operators.new_operator')}</h2>
                <button type="button" onClick={() => setShowNewOperatorForm(false)} className="text-gray-400 hover:text-gray-500">
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-2">
                <div className="sm:col-span-1">
                  <label htmlFor="first-name" className="block text-sm font-medium text-gray-700">
                    {i18n.t('list_operators.name')}
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="first-name"
                      id="first-name"
                      autoComplete="given-name"
                      value={userName}
                      onChange={e => setUserName(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                    {i18n.t('list_operators.surname')}
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="last-name"
                      id="last-name"
                      value={userLastname}
                      onChange={e => setUserLastname(e.target.value)}
                      autoComplete="family-name"
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="sm:col-span-1">
                  <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                    {i18n.t('list_operators.email')}
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      name="username"
                      id="username"
                      autoComplete="username"
                      value={userEmail}
                      onChange={e => setUserEmail(e.target.value)}
                      onBlur={async e => {
                        const email = e.target.value.trim()
                        if (!email.includes('@')) return
                        try {
                          if ((await getLoginMethod(email)) === 'sso') setSsoEmail(email)
                        } catch (_) {
                          // Discovery down: keep the password field; the backend ignores it for SSO emails.
                        }
                      }}
                      className="block w-full min-w-0 flex-1 rounded-none border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    />
                  </div>
                  <a
                    onClick={() => {
                      navigator.clipboard.writeText(userEmail)
                      setShowClipboardCopied()
                    }}
                  >
                    {i18n.t('list_operators.copy_email')}
                  </a>
                </div>
                {userEmailIsSso ? (
                  <div className="sm:col-span-1">
                    <p className="text-sm text-gray-500">{i18n.t('list_operators.sso_no_password')}</p>
                  </div>
                ) : (
                  <div className="sm:col-span-1">
                    <label htmlFor="last-name" className="block text-sm font-medium text-gray-700">
                      {i18n.t('list_operators.password')}
                    </label>
                    <div className="mt-1">
                      <input
                        type="text"
                        name="last-name"
                        id="last-name"
                        value={userPassword}
                        onChange={e => setUserPassword(e.target.value)}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <a onClick={() => generatePwd()}>{i18n.t('list_operators.generate_password')}</a>
                    <span className="text-blue-600"> | </span>
                    <a
                      onClick={() => {
                        navigator.clipboard.writeText(userPassword)
                        setShowClipboardCopied()
                      }}
                    >
                      {i18n.t('list_operators.copy_password')}
                    </a>
                  </div>
                )}
                <div className="sm:col-span-2">
                  <label htmlFor="warehouses" className="block text-sm font-medium text-gray-700">
                    {i18n.t('list_operators.warehouses')} <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder={i18n.t('list_operators.select_warehouses')}
                      className="w-full"
                      value={selectedWarehouses}
                      onChange={values => {
                        setSelectedWarehouses(values)
                        if (values && values.length > 0) {
                          setWarehousesError(false)
                        }
                      }}
                      loading={isLoadingWarehouses}
                      getPopupContainer={trigger => trigger.parentElement}
                      disabled={isLoadingWarehouses || !warehousesData?.warehouses}
                      status={warehousesError ? 'error' : ''}
                    >
                      {warehousesData?.warehouses?.map(warehouse => (
                        <Select.Option key={warehouse.id} value={warehouse.id}>
                          {warehouse.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  {warehousesError && <p className="mt-1 text-sm text-red-500">{i18n.t('list_operators.warehouses_required')}</p>}
                </div>
                <div className="sm:col-span-2">
                  <label htmlFor="roles" className="block text-sm font-medium text-gray-700">
                    {i18n.t('list_operators.roles')}
                  </label>
                  <div className="mt-1">
                    <Select
                      mode="multiple"
                      allowClear={false}
                      placeholder={i18n.t('list_operators.roles')}
                      className="w-full"
                      value={selectedRoles.filter(selected_role => OPERATOR_ROLES.some(operator_role => operator_role.role === selected_role))}
                      onChange={values => {
                        const base = values?.length > 0 ? values : ['ROLE_OPERATOR']
                        const withOperator = base.includes('ROLE_OPERATOR') ? base : ['ROLE_OPERATOR', ...base]
                        setSelectedRoles(prev => {
                          const next = [...withOperator]
                          // Warehouse admins get OMS access via the scoped add-on, never ROLE_CUSTOMER —
                          // so drop a lingering ROLE_CUSTOMER when WH admin is selected (its checkbox is
                          // hidden for WH admins and would otherwise become an invisible, unremovable grant).
                          if (prev.includes('ROLE_CUSTOMER') && !withOperator.includes('ROLE_WH_ADMIN')) next.push('ROLE_CUSTOMER')
                          // The OMS add-on is only valid alongside ROLE_WH_ADMIN; drop it if WH admin was removed.
                          if (prev.includes('ROLE_WH_ADMIN_OMS_ACCESS') && withOperator.includes('ROLE_WH_ADMIN')) {
                            next.push('ROLE_WH_ADMIN_OMS_ACCESS')
                          }
                          return next
                        })
                      }}
                      getPopupContainer={trigger => trigger.parentElement}
                    >
                      {assignableNewOperatorRoles().map(role => (
                        <Select.Option key={role.role} value={role.role} disabled={role.role === 'ROLE_OPERATOR'}>
                          {role.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  {selectedRoles.includes('ROLE_WH_ADMIN') && (
                    <div className="mt-2 rounded-md bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800">
                      {i18n.t('list_operators.wh_admin_elevated_notice')}
                    </div>
                  )}
                  <div className="mt-2">
                    {selectedRoles.includes('ROLE_WH_ADMIN') ? (
                      // Warehouse admins get OMS access via the dedicated add-on (scoped to their
                      // warehouse's stores), never through ROLE_CUSTOMER.
                      <Checkbox
                        label={i18n.t('list_operators.wh_admin_oms_access')}
                        checked={selectedRoles.includes('ROLE_WH_ADMIN_OMS_ACCESS')}
                        onChange={isChecked =>
                          setSelectedRoles(prev =>
                            isChecked ? [...prev, 'ROLE_WH_ADMIN_OMS_ACCESS'] : prev.filter(selected_role => selected_role !== 'ROLE_WH_ADMIN_OMS_ACCESS'),
                          )
                        }
                      />
                    ) : (
                      <Checkbox
                        label={i18n.t('list_operators.customer_app_access')}
                        checked={selectedRoles.includes('ROLE_CUSTOMER')}
                        onChange={isChecked =>
                          setSelectedRoles(prev => (isChecked ? [...prev, 'ROLE_CUSTOMER'] : prev.filter(selected_role => selected_role !== 'ROLE_CUSTOMER')))
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
              <ButtonLoader loading={loadOperatorCreation} className="w-full whitespace-nowrap mt-4" type="primary" onClick={() => handleOnCreateOperator()}>
                {i18n.t('list_operators.create_operator')}
              </ButtonLoader>
            </DialogView>

            <NewTable
              data={memoizedTableData}
              columns={memoizedColumns}
              selectable={false}
              cursorPointer={false}
              showHeader
              showLoader={isLoading || isFetching}
              isFetching={isFetching}
              emptyTableText={i18n.t('list_operators.empty_table_text')}
              paginationMeta={data?.meta}
              onPaginationChange={requestedPage => {
                setSearchParams({ ...searchParams, page: requestedPage })
              }}
              onPageSizeChange={pageSize => {
                setSearchParams({ ...searchParams, per_page: pageSize.id, page: 1 })
                dispatch(setOperatorsPageSize(pageSize.id))
              }}

              handleResetFilters={handleResetFilters}
              filtersData={filtersData}
              onSortChange={onSortChange}
              hasExport={false}
              onSearchHandler={updateFiltersData}
            />
          </>
        )}
      </PageView>
      <Switch>
        <Route exact path={CONFIGURATIONS_OPERATORS_DETAIL}>
          <SlidePanel title="Ver Operador" referrer={CONFIGURATIONS_OPERATORS.replace(':warehouse_id', warehouse_id)} onClose={() => refetch()}>
            <OperatorDetailContainer />
          </SlidePanel>
        </Route>
      </Switch>
    </>
  )
}
